/**
 * Colorful iPhone-style message mockup used on the Example SMS Coupon
 * sheet, the Send-a-Text-Blast preview, and the Live Pitch page.
 *
 * Colors flow from an `accent` class (e.g. "bg-blue-600 text-white") so each
 * offer can theme its own bubble header + footer badge.
 *
 * Optional props to make the pitch pop:
 *   • logoUrl        — dealership logo shown in the header circle (falls back to initials)
 *   • scheduleUrl    — renders a big blue "Tap to Schedule" CTA button under the text
 *   • callUrl        — renders a green "Tap to Speak Live" CTA button under the text
 *   • body           — plain-text message; URLs inside are auto-highlighted in blue
 */

// Match http(s):// links AND bare "drvlgc.co/..." / "dcln.link/..." style shortlinks
const URL_RE = /(https?:\/\/[^\s]+|(?:d-drv\.co|dcln\.link|d-drv\.co)\/[a-z0-9\-/]+)/gi;

function HighlightedBody({ text }) {
  if (!text) return null;
  const parts = String(text).split(URL_RE);
  return (
    <>
      {parts.map((p, i) => {
        if (i % 2 === 1) {
          return (
            <span
              key={i}
              className="text-blue-600 dark:text-blue-400 underline underline-offset-2 font-semibold decoration-blue-400/60 break-all"
            >
              {p}
            </span>
          );
        }
        return <span key={i}>{p}</span>;
      })}
    </>
  );
}

export default function PhoneMock({
  dealer,
  from,
  body,
  sent = "Now",
  label,
  accent = "bg-blue-600 text-white",
  logoUrl,
  scheduleUrl,
  callUrl,
  testid,
}) {
  const initials = (dealer || "DD").substring(0, 2).toUpperCase();
  return (
    <div
      className="rounded-[2rem] border-8 border-neutral-800 bg-white overflow-hidden shadow-xl w-[300px] shrink-0 mx-auto"
      data-testid={testid}
    >
      {/* Status bar */}
      <div className="bg-neutral-800 text-white text-[0.6rem] px-4 py-1.5 flex justify-between items-center">
        <span>{sent}</span>
        <span>•••</span>
      </div>
      {/* Header */}
      <div className={`px-3 py-2 border-b border-neutral-200 flex items-center gap-2 ${accent}`}>
        <div className="w-7 h-7 rounded-full bg-white/95 grid place-items-center overflow-hidden text-[0.55rem] font-bold text-neutral-800 shrink-0">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={`${dealer || "Dealership"} logo`}
              className="w-full h-full object-contain"
              onError={(e) => {
                // Fallback to initials if the logo URL fails
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            initials
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[0.65rem] font-bold text-white truncate">{dealer || "Dealership"}</div>
          <div className="text-[0.55rem] text-white/80 truncate">{from}</div>
        </div>
      </div>
      {/* Message bubble */}
      <div className="bg-neutral-100 px-3 py-4 min-h-[220px]">
        <div className="text-[0.55rem] text-neutral-500 text-center mb-2">Text Message · Today</div>
        <div className="bg-neutral-200 text-neutral-900 rounded-2xl rounded-bl-md px-3 py-2 text-[0.7rem] leading-snug max-w-[92%] break-words shadow-sm whitespace-pre-line">
          <HighlightedBody text={body} />
        </div>

        {/* Tap-to-action CTA buttons — these appear right under the text, exactly
            where a real iPhone renders link previews. Great for pointing out
            during a live pitch: "your customer taps HERE to book." */}
        {(scheduleUrl || callUrl) && (
          <div className="mt-3 space-y-2" data-testid={testid ? `${testid}-ctas` : undefined}>
            {scheduleUrl && (
              <button
                type="button"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white text-[0.7rem] font-semibold rounded-xl px-3 py-2 flex items-center justify-center gap-1.5 shadow-sm active:translate-y-[1px] transition-transform"
                data-testid={testid ? `${testid}-schedule` : "phonemock-schedule"}
              >
                <span aria-hidden>📅</span>
                Tap to Schedule
              </button>
            )}
            {callUrl && (
              <button
                type="button"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-[0.7rem] font-semibold rounded-xl px-3 py-2 flex items-center justify-center gap-1.5 shadow-sm active:translate-y-[1px] transition-transform"
                data-testid={testid ? `${testid}-call` : "phonemock-call"}
              >
                <span aria-hidden>📞</span>
                Tap to Speak Live
              </button>
            )}
          </div>
        )}
      </div>
      {/* Footer badge */}
      {label && (
        <div className="border-t border-neutral-200 bg-neutral-50 px-3 py-1.5 text-center">
          <span className={`inline-block text-[0.55rem] px-2 py-0.5 rounded-full font-semibold ${accent}`}>
            {label}
          </span>
        </div>
      )}
    </div>
  );
}
